import ReactEcharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

import { ContentCard } from '@/components/customComponents'

import {
  barChartOptions,
  lineChartOptions,
  stackedBarChartOptions,
} from '@/constants/CHART_CONSTANTS'
import { toast, useSpend } from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { SeasonalityModel } from '@/types/clarityLens'
import { dateToString } from '@/utils'
import { useEffect } from 'react'

interface SpendTabProps {
  itemName: string
}

export const SpendTab = ({ itemName }: SpendTabProps) => {
  const { t } = useTranslation()
  // Get filter state from Redux
  const { selectedBranch, date } = useReduxBaseFilter('spend-lens-item')
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('spend-lens-item')
  )

  const {
    supplierBreakdownChartData,
    storeBreakdownChartData,
    seasonalityChartData,
    loading: spendLoading,
    error: spendError,
    refetch: spendRefetch,
  } = useSpend(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing spend data...')
      spendRefetch()
    }
  }, [refreshTrigger, spendRefetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (spendError) {
      toast({
        title: t('common.errors.serverError'),
        description: spendError?.message,
        variant: 'destructive',
      })
    }
  }, [spendError])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* TODO: product name */}
        <ContentCard
          title={t('clarityLens.spendLens.charts.supplierBreakdown')}
          loading={spendLoading}
          isEmpty={!supplierBreakdownChartData}
        >
          {supplierBreakdownChartData &&
            supplierBreakdownChartData.rawData.length > 0 && (
              <ReactEcharts
                option={getStackedBarChartData({
                  rawData: supplierBreakdownChartData.rawData,
                  seriesNames: supplierBreakdownChartData.seriesNames,
                  xAxisNames: supplierBreakdownChartData.xAxisNames,
                })}
              />
            )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.spendLens.charts.storeBreakdown')}
          loading={spendLoading}
          isEmpty={!storeBreakdownChartData}
        >
          {storeBreakdownChartData && (
            <ReactEcharts
              option={getBarChartData(storeBreakdownChartData.data)}
            />
          )}
        </ContentCard>
      </div>
      <div>
        <ContentCard
          title={t('clarityLens.spendLens.charts.seasonality')}
          loading={spendLoading}
          isEmpty={!seasonalityChartData}
        >
          {seasonalityChartData && (
            <ReactEcharts
              option={getLineChartData(seasonalityChartData.data)}
            />
          )}
        </ContentCard>
      </div>
    </div>
  )
}

const getStackedBarChartData = (primaryData: {
  rawData: number[][]
  seriesNames: string[]
  xAxisNames: string[]
}) => {
  return stackedBarChartOptions(
    primaryData.rawData,
    primaryData.seriesNames,
    primaryData.xAxisNames,
    '40%',
    'money'
  )
}

const getBarChartData = (
  storeData: { branchName: string; grossSalesAmount: number }[]
) => {
  const rawData = [storeData.map((item) => item.grossSalesAmount)]
  const seriesNames = ['Spend']
  const xAxisNames = storeData.map((item) => item.branchName)

  return barChartOptions(rawData, seriesNames, xAxisNames, 'money')
}

const getLineChartData = (seasonalityData: SeasonalityModel) => {
  // Safety check for empty data
  if (!seasonalityData || Object.keys(seasonalityData).length === 0) {
    return lineChartOptions([], [], [], 'money')
  }

  const periods = Object.keys(seasonalityData) // These will be our line names/series
  const allMonths = new Set<string>()

  // Collect all unique months (these will be our X-axis data points)
  periods.forEach((period) => {
    if (seasonalityData[period] && Array.isArray(seasonalityData[period])) {
      seasonalityData[period].forEach((item) => {
        if (item && item.month) {
          allMonths.add(item.month)
        }
      })
    }
  })

  const monthNames = Array.from(allMonths) // Months should be in their natural order

  // Safety check for no months found
  if (monthNames.length === 0) {
    return lineChartOptions([], [], [], 'money')
  }

  // Create raw data for each period (line)
  const rawData = periods.map((period) => {
    return monthNames.map((month) => {
      const monthData = seasonalityData[period]?.find(
        (item) => item?.month === month
      )
      return monthData ? monthData.grossSalesAmount : 0
    })
  })

  return lineChartOptions(rawData, periods, monthNames, 'money')
}
